import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import styles from './Popover2.js';
import '../../hooks/index.js';
import '../../utils/index.js';
import useClickOutsidePortals from '../../hooks/useClickOutsidePortals/useClickOutsidePortals.js';
import useId from '../../hooks/useId/useId.js';
import Popper from '../../utils/Popper/Popper.js';
import mergeRefs from '../../utils/mergeRefs/mergeRefs.js';

const Popover = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  let {
    show = false,
    onClose,
    content,
    placement = 'top',
    children,
    zIndex = 1000,
    className,
    portalTarget,
    isArrowRemoved = false,
    onClickOutside,
    onClick,
    onKeyDown,
    style = {},
    id: externalId,
    ...rest
  } = _ref;
  const classes = classNames(styles.popover, className);
  const anchorRef = useRef();
  const popperRef = useRef(null);
  const preventClickOutside = useClickOutsidePortals(e => show && (onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside(e)));
  const [isInDOM, setIsInDOM] = useState(false);
  const popoverId = useId(externalId);

  // Focus lock can't be enabled without `onClose` function because keyboard users need a way to exit the focus lock
  const isFocusLockEnabled = typeof onClose === 'function';
  useEffect(() => {
    /*
     * Only focuses the anchor element if the `show` prop is false and the `isInDom` prop is still true.
     * This indicates that the state moved from show to hide.
     * We don't want to focus the anchor element on mount because that will mess up focus management, which is why we are explicitely doing a show-to-hide check.
     * `react-focus-lock` handles this logic with the `returnFocus` prop, but it doesn't always work.
     * `returnFocus` doesn't work when:
     * 1. A user in Chrome clicks on 2 or more `Popover` anchor elements and then presses the `Escape` key. Even though buttons are focused on `mousedown` in Chrome by default, the previously opened `Popover` grabs focus right after the next anchor element is clicked, and therfore the previously opened `Popover` becomes the place to return focus, which is not what we want.
     * 2. A user in Safari clicks on 1 or more `Popover` anchor elements and then presses the `Escape` key. This is because buttons aren't focused on `click` or `mousedown` in Safari by default.
     */
    if (!show && isInDOM) {
      var _anchorRef$current;
      // Prevents scroll so that the page does not scroll to the anchor element if the anchor element is out of view
      (_anchorRef$current = anchorRef.current) === null || _anchorRef$current === void 0 || _anchorRef$current.focus({
        preventScroll: true
      });
    }
    setIsInDOM(show);
  }, [show, isInDOM]);
  const handlePopoverContainerClick = e => {
    onClick === null || onClick === void 0 || onClick(e);
  };
  const handlePopoverContainerKeyDown = e => {
    if (e.key === 'Escape') {
      e.stopPropagation(); // Prevent event from closing modals or popovers
      e.preventDefault(); // Prevent Safari from exiting full-screen mode
      onClose === null || onClose === void 0 || onClose(e);
    }
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(e);
  };
  return /*#__PURE__*/React__default.createElement(Popper, {
    open: show,
    isFocusLocked: true,
    focusLockProps: {
      disabled: !isFocusLockEnabled,
      focusOptions: {
        preventScroll: true
      }
    },
    placement: placement,
    anchor: /*#__PURE__*/React__default.cloneElement(children, {
      'aria-expanded': show,
      'aria-controls': show ? popoverId : undefined,
      onMouseDown: e => {
        var _children$props$onMou, _children$props;
        preventClickOutside();
        (_children$props$onMou = (_children$props = children.props).onMouseDown) === null || _children$props$onMou === void 0 || _children$props$onMou.call(_children$props, e);
      },
      ref: mergeRefs(anchorRef, children === null || children === void 0 ? void 0 : children.ref)
    }),
    popper: /*#__PURE__*/React__default.createElement("div", _extends({}, rest, {
      ref: mergeRefs(ref, popperRef),
      style: {
        zIndex,
        ...style
      },
      className: classes,
      onMouseDown: preventClickOutside,
      onClick: handlePopoverContainerClick,
      onKeyDown: handlePopoverContainerKeyDown,
      id: popoverId,
      role: "dialog",
      tabIndex: -1 // Allows focus lock to lock focus in the Popover content even when there are no interactive elements
      ,
      "aria-modal": isFocusLockEnabled ? 'true' : undefined
    }), content),
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, 7]
      }
    }, {
      name: 'flip',
      options: {
        flipVariations: false
      }
    }],
    arrow: isArrowRemoved ? undefined : /*#__PURE__*/React__default.createElement("div", {
      className: styles.arrow
    }, /*#__PURE__*/React__default.createElement("div", null)),
    portalTarget: portalTarget
  });
});

export { Popover as default };
